import { ElementRef, NgZone } from '@angular/core';

/**
 * The expanders' popover shape (owner 2026-10-15): the box (`.tsx`) clipped to
 * its rounded rectangle plus a macOS-popover arrow under the clicked card
 * (--arrow-x, set by the gallery page on the expander row), so the arrow is the
 * box itself, its ground and wallpaper with no seam; the same path drawn as
 * `.expander-outline` gives it its edge. Redrawn when the box resizes and when
 * the page calls `schedule()` (another card of the same row).
 */
export class PopoverShape {
  private observer?: ResizeObserver;
  private frame?: number;

  constructor( private host: ElementRef<HTMLElement>, private zone: NgZone ) {
  }

  attach(): void {
    if ( typeof ResizeObserver === 'undefined' ) {
      return;
    }
    this.zone.runOutsideAngular( () => {
      this.observer = new ResizeObserver( () => this.schedule() );
      this.observer.observe( this.host.nativeElement );
    } );
    this.schedule();
  }

  destroy(): void {
    this.observer?.disconnect();
    if ( this.frame ) {
      cancelAnimationFrame( this.frame );
    }
  }

  schedule(): void {
    if ( typeof requestAnimationFrame === 'undefined' ) {
      return;
    }
    this.zone.runOutsideAngular( () => {
      if ( this.frame ) {
        cancelAnimationFrame( this.frame );
      }
      this.frame = requestAnimationFrame( () => {
        this.frame = undefined;
        this.apply();
      } );
    } );
  }

  private apply(): void {
    const host = this.host.nativeElement;
    const box = host.querySelector<HTMLElement>( '.tsx' );
    if ( !box ) {
      return;
    }
    const w = box.offsetWidth;
    const h = box.offsetHeight;
    if ( !w || !h ) {
      return;
    }
    const arrowH = 18, halfW = 20, r = Math.min( 28, w / 2, ( h - arrowH ) / 2 );
    const rawX = parseFloat( getComputedStyle( host ).getPropertyValue( '--arrow-x' ) );
    // the arrow's foot clear of the rounded corners
    const x = Math.min( Math.max( isNaN( rawX ) ? w / 2 : rawX - box.getBoundingClientRect().left
      + ( host.parentElement?.getBoundingClientRect().left ?? 0 ), r + halfW ), w - r - halfW );
    const t = arrowH;
    // the arrow's sides curve out of the box's edge and up to a pointed tip,
    // softly rounded (owner 2026-10-15)
    const path = `M ${ r } ${ t } L ${ x - halfW } ${ t }`
      + ` C ${ x - halfW + 9 } ${ t } ${ x - 5 } 0.5 ${ x } 0.5`
      + ` C ${ x + 5 } 0.5 ${ x + halfW - 9 } ${ t } ${ x + halfW } ${ t }`
      + ` L ${ w - r } ${ t } A ${ r } ${ r } 0 0 1 ${ w } ${ t + r }`
      + ` L ${ w } ${ h - r } A ${ r } ${ r } 0 0 1 ${ w - r } ${ h }`
      + ` L ${ r } ${ h } A ${ r } ${ r } 0 0 1 0 ${ h - r }`
      + ` L 0 ${ t + r } A ${ r } ${ r } 0 0 1 ${ r } ${ t } Z`;
    box.style.clipPath = `path('${ path }')`;
    host.querySelector( '.expander-outline path' )?.setAttribute( 'd', path );
  }
}
